"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { Contact, PHONE_TYPES, PhoneType } from "@/schema"
import { useContactStore } from "@/stores/use-contact-store"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { usePinnedDialogStore } from "@/stores/use-pinned-dialog-store"
import { isValidPhone } from "@/utils/phone"
import { zodResolver } from "@hookform/resolvers/zod"
import { BookSelectField, TextField } from "@ui/forms"
import { PhoneInput } from "@ui/phone-input"
import {
  Button,
  DialogFooter,
  Form,
  FormField,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ui/shad"
import { PictureInPicture2, X } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { ALL_BOOKS_SQID, isAllBooksId } from "@/lib/all-books"
import { useCreateContact } from "@/hooks/api/contacts"
import { useBookId } from "@/hooks/use-book-id"
import { useSingleFlight } from "@/hooks/use-single-flight"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"

const createFormSchema = (requireBookId: boolean) =>
  z
    .object({
      firstName: z.string().optional().default(""),
      lastName: z.string().optional().default(""),
      email: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
      phone: z
        .string()
        .refine((val) => !val || isValidPhone(val), { message: "Please enter a valid phone number" })
        .optional()
        .or(z.literal("")),
      phoneExtension: z.string().regex(/^\d*$/, "Extension must contain only numbers").optional().default(""),
      phoneType: z.enum(PHONE_TYPES),
      bookId: requireBookId ? z.string().min(1, "Book is required") : z.string().optional(),
    })
    .refine((data) => data.firstName?.trim() || data.email?.trim(), {
      message: "Either first name or email is required",
      path: ["firstName"],
    })

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

const normalizeDefault = (value: unknown) => (typeof value === "string" ? value : "")

export function ContactForm({
  id = "",
  defaultEmail = "",
  defaultPhone = "",
  defaultFirstName = "",
  defaultLastName = "",
  onCreated,
}: {
  id?: string
  defaultEmail?: string
  defaultPhone?: string
  defaultFirstName?: string
  defaultLastName?: string
  onCreated?: (contact: Contact) => void
}) {
  const dialogStore = useGlobalDialogStore()
  const { pinnedDialogs, unpinDialog, closeDialog } = usePinnedDialogStore()
  const [draftId, setDraftId] = useState(id)
  const { drafts, createDraft, updateDraft, removeDraft } = useContactStore()
  const createContact = useCreateContact({ skipDefaultSuccessToast: true })
  const { begin: beginCreate, end: endCreate } = useSingleFlight()
  const isPinned = pinnedDialogs.some((dialog) => dialog.id === draftId)
  const [createMore, setCreateMore] = useState(false)
  const firstNameInputRef = useRef<HTMLInputElement | null>(null)
  const autoFocusedDraftIdRef = useRef<string | null>(null)
  const router = useRouter()
  const bookId = useBookId()
  const showBookSelector = isAllBooksId(bookId)
  const formSchema = useMemo(() => createFormSchema(showBookSelector), [showBookSelector])

  useEffect(() => {
    if (!draftId) {
      const newDraftId = createDraft({
        firstName: defaultFirstName || "",
        lastName: defaultLastName || "",
        email: defaultEmail || "",
        phone: defaultPhone || "",
      })
      setDraftId(newDraftId)
    }
  }, [draftId, createDraft, defaultFirstName, defaultLastName, defaultEmail, defaultPhone])

  const draft = draftId ? drafts[draftId] : null

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: draft?.firstName || defaultFirstName || "",
      lastName: draft?.lastName || defaultLastName || "",
      email: draft?.email || defaultEmail || "",
      phone: draft?.phone || defaultPhone || "",
      phoneExtension: draft?.phoneExtension || "",
      phoneType: draft?.phoneType || "mobile",
    },
  })

  useEffect(() => {
    if (!draftId || !draft) return

    form.reset({
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      phone: draft.phone ?? "",
      phoneExtension: draft.phoneExtension ?? "",
      phoneType: draft.phoneType,
      bookId: draft.bookId ?? "",
    })

    setCreateMore(draft.createMore ?? false)
  }, [draftId, draft, form])

  useEffect(() => {
    if (!draftId || !draft || autoFocusedDraftIdRef.current === draftId) return

    const focusFrame = window.requestAnimationFrame(() => {
      firstNameInputRef.current?.focus()
      autoFocusedDraftIdRef.current = draftId
    })

    return () => window.cancelAnimationFrame(focusFrame)
  }, [draftId, draft])

  const onValueChange = (values: Partial<FormValues>) => {
    if (!draftId) return
    updateDraft(draftId, values)
  }

  const onSubmit = (data: FormValues, effectiveCreateMore?: boolean) => {
    if (!draftId) return
    // Guard the handler, not just the button — ⌘+Enter reaches this directly.
    if (createContact.isPending) return

    const shouldCreateMore = effectiveCreateMore ?? createMore

    const personAttributes = {
      first_name: data.firstName,
      last_name: data.lastName,
      email_addresses_attributes: data.email
        ? [
            {
              email: data.email,
              is_primary: true,
            },
          ]
        : [],
      phone_numbers_attributes: data.phone
        ? [
            {
              unformatted_phone: data.phone,
              extension: data.phoneExtension || undefined,
              is_primary: true,
              phone_type: data.phoneType,
            },
          ]
        : undefined,
    }

    if (!beginCreate()) return

    createContact.mutate(
      {
        contact: {
          person_attributes: personAttributes,
        },
        overrideBookId: data.bookId,
      },
      {
        onSettled: endCreate,
        onSuccess: (contact) => {
          const targetBookId = data.bookId ? ALL_BOOKS_SQID : bookId
          const contactPath = `/${targetBookId}/contacts/${contact.id}?secondary=details`

          if (onCreated) {
            onCreated(contact)
          } else {
            toast.success("Contact created")
          }

          if (shouldCreateMore) {
            const newDraftId = createNewDraft(shouldCreateMore)
            switchToNewDraft(newDraftId, draftId)
          } else {
            if (!onCreated) {
              router.push(contactPath)
            }
            resetForm()
          }
        },
      }
    )
  }

  const resetForm = () => {
    if (draftId) {
      removeDraft(draftId)
    }

    if (isPinned) {
      closeDialog(draftId)
    } else {
      dialogStore.closeDialog()
    }
  }

  const createNewDraft = (preserveCreateMore: boolean) => {
    const newDraftId = createDraft()

    updateDraft(newDraftId, {
      createMore: preserveCreateMore,
    })

    return newDraftId
  }

  const switchToNewDraft = (newDraftId: string, oldDraftId: string) => {
    setDraftId(newDraftId)
    autoFocusedDraftIdRef.current = null

    if (isPinned) {
      const { updateDialogId } = usePinnedDialogStore.getState()
      updateDialogId(oldDraftId, newDraftId, "contact")
    }

    removeDraft(oldDraftId)
  }

  const handlePin = () => {
    if (!draftId) return

    if (isPinned) {
      unpinDialog(draftId)
    } else {
      dialogStore.pinDialog(draftId, "contact")
    }
  }

  const handleClose = () => {
    if (!draftId) return

    if (isPinned) {
      closeDialog(draftId)
      resetForm()
    } else {
      dialogStore.closeDialog()
    }
  }

  const handleCreateMoreChange = (checked: boolean) => {
    if (!draftId) return
    setCreateMore(checked)
    updateDraft(draftId, { createMore: checked })
  }

  if (!draft) return null

  return (
    <CustomDialogContent title="Create new contact">
      <div className="relative flex h-full w-full flex-col">
        <div className="flex h-12 items-center justify-between gap-2 border-b pr-3 pl-6">
          <div className="font-semibold">Add contact</div>
          <div className="flex items-center justify-end gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="iconSm" variant={isPinned ? "secondary" : "ghost"} onClick={handlePin}>
                  <PictureInPicture2 className="text-icon hover:text-icon-hover" />
                  <span className="sr-only">{isPinned ? "Unpin" : "Pin to screen"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isPinned ? "Unpin" : "Pin to screen"}</TooltipContent>
            </Tooltip>
            <Button size="iconSm" variant="ghost" onClick={handleClose}>
              <X className="text-icon hover:text-icon-hover" />
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => onSubmit(values, createMore))}
            onKeyDown={(e) => {
              if (e.metaKey && e.key === "Enter") {
                e.preventDefault()
                const nextCreateMore = e.shiftKey ? !createMore : undefined
                if (nextCreateMore !== undefined) {
                  handleCreateMoreChange(nextCreateMore)
                }
                form.handleSubmit((values) => onSubmit(values, nextCreateMore))()
              }
            }}
            className="flex flex-1 flex-col gap-4 p-4"
          >
            {showBookSelector && (
              <FormField
                control={form.control}
                name="bookId"
                render={({ field }) => (
                  <BookSelectField<FormValues>
                    id="bookId"
                    field={{
                      ...field,
                      onChange: (value: string) => {
                        field.onChange(value)
                        onValueChange({ bookId: value })
                      },
                    }}
                  />
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="firstName"
                render={({ field }) => (
                  <TextField
                    id="firstName"
                    label="First name"
                    placeholder="First name"
                    autoComplete="off"
                    field={{
                      ...field,
                      ref: (node) => {
                        field.ref(node)
                        firstNameInputRef.current = node
                      },
                      onChange: (e) => {
                        field.onChange(e)
                        onValueChange({ firstName: e.target.value })
                      },
                    }}
                    error={!!form.formState.errors.firstName?.message}
                  />
                )}
              />

              <FormField
                name="lastName"
                render={({ field }) => (
                  <TextField
                    id="lastName"
                    label="Last name"
                    placeholder="Last name"
                    autoComplete="off"
                    field={{
                      ...field,
                      onChange: (e) => {
                        field.onChange(e)
                        onValueChange({ lastName: e.target.value })
                      },
                    }}
                    error={!!form.formState.errors.lastName?.message}
                  />
                )}
              />
            </div>

            <FormField
              name="email"
              render={({ field }) => (
                <TextField
                  id="email"
                  label="Email"
                  placeholder="Email address"
                  autoComplete="off"
                  field={{
                    ...field,
                    onChange: (e) => {
                      field.onChange(e)
                      onValueChange({ email: e.target.value })
                    },
                  }}
                  error={!!form.formState.errors.email?.message}
                />
              )}
            />

            <div className="grid grid-cols-3 gap-2">
              <FormField
                name="phone"
                render={({ field }) => (
                  <div className="col-span-2 space-y-1">
                    <Label htmlFor="phone">Phone</Label>
                    <PhoneInput
                      value={field.value ?? ""}
                      onChange={(val) => {
                        field.onChange(val)
                        onValueChange({ phone: val })
                      }}
                      extension={form.watch("phoneExtension") ?? ""}
                      onExtensionChange={(value) => {
                        form.setValue("phoneExtension", value, { shouldValidate: true })
                        onValueChange({ phoneExtension: value })
                      }}
                    />
                    {form.formState.errors.phone?.message ? (
                      <span className="text-destructive text-xs">{form.formState.errors.phone.message}</span>
                    ) : null}
                  </div>
                )}
              />
              <FormField
                name="phoneType"
                render={({ field }) => (
                  <div className="space-y-1">
                    <Label>Type</Label>
                    <Select
                      value={field.value}
                      onValueChange={(value: PhoneType) => {
                        field.onChange(value)
                        onValueChange({ phoneType: value })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHONE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />
            </div>
          </form>
        </Form>

        <DialogFooter className="round flex w-full items-center justify-end p-3">
          <div className="-my-1! flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={createMore} onCheckedChange={handleCreateMoreChange} />
              <span className="text-foreground text-xs">Create more</span>
            </div>
            <Button
              onClick={() => form.handleSubmit((values) => onSubmit(values, createMore))()}
              disabled={createContact.isPending}
            >
              Add contact
            </Button>
          </div>
        </DialogFooter>
      </div>
    </CustomDialogContent>
  )
}

export function useCreateContactDialog() {
  const { openDialog, closeDialog } = useGlobalDialogStore()

  const open = useCallback(
    (
      options: {
        defaultEmail?: unknown
        defaultPhone?: unknown
        defaultFirstName?: unknown
        defaultLastName?: unknown
        onCreated?: (contact: Contact) => void
      } = {}
    ) => {
      const { defaultEmail = "", defaultPhone = "", defaultFirstName = "", defaultLastName = "", onCreated } = options

      openDialog({
        variant: "custom",
        content: (
          <ContactForm
            defaultEmail={normalizeDefault(defaultEmail)}
            defaultPhone={normalizeDefault(defaultPhone)}
            defaultFirstName={normalizeDefault(defaultFirstName)}
            defaultLastName={normalizeDefault(defaultLastName)}
            onCreated={onCreated}
          />
        ),
        dialogContentClasses: "max-w-(--breakpoint-sm)",
      })
    },
    [openDialog]
  )

  return {
    open,
    close: () => {
      closeDialog()
    },
  }
}

// Legacy export for backwards compatibility
export const AddContactDialog = ContactForm
